import React, { useState, useEffect } from 'react';
import {
  getServices, ServiceData, getServiceStats, getReligionDistribution,
  createService, updateService,
  getConfig, updateConfig,
} from '../services/adminService';
import {
  pageContainer, cardStyle, statsGrid, labelStyle, valueStyle,
  subTextStyle, sectionTitle, thStyle, tdStyle, badgeStyle, tableSearch, smallSelect,
} from '../styles/theme';
import { useDebounce, useToast } from '../utils/useAdminPage';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';
import { exportToCSV } from '../utils/csvExport';

const ServiceManagement = () => {
  const [services, setServices] = useState<ServiceData[]>([]);
  const [serviceStats, setServiceStats] = useState<any[]>([]);
  const [religionData, setReligionData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('');
  const [page, setPage]       = useState(1);
  const [activeTab, setActiveTab] = useState<'standard' | 'custom'>('standard');
  const pageSize = 10;

  const [editingService, setEditingService] = useState<ServiceData | null>(null);
  const [reviewingService, setReviewingService] = useState<ServiceData | null>(null);
  const [editForm, setEditForm] = useState({ basePrice: 0, startRange: 0, endRange: 0, durationMin: 0, platformFix: 0, platformPercentage: 0, isActive: true, isCustom: false, isApproved: true, iconUrl: '' });

  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', religion: '', basePrice: 0, startRange: 0, endRange: 0, durationMin: 60, platformFix: 0, platformPercentage: 0, isCustom: false, iconUrl: '' });

  const [isManagingReligions, setIsManagingReligions] = useState(false);
  const [religions, setReligions] = useState<string[]>([]);
  const [newReligion, setNewReligion] = useState('');
  const [processing, setProcessing] = useState(false);

  const dSearch = useDebounce(search, 350);
  const { toast, showToast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    const [svcs, stats, relDist] = await Promise.all([
      getServices(),
      getServiceStats(),
      getReligionDistribution(),
    ]);
    setServices(svcs || []);
    setServiceStats(stats || []);
    if (relDist?.scholars) setReligionData(relDist.scholars);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Client-side filter (services endpoint doesn't have server-side filtering yet)
  const filtered = services.filter(s => {
    const isTabMatch = activeTab === 'standard' ? !s.isCustom : s.isCustom;
    const matchSearch = !dSearch || s.name.toLowerCase().includes(dSearch.toLowerCase()) || s.religion.toLowerCase().includes(dSearch.toLowerCase());
    const matchFilter = !filter || (filter === 'active' && s.isActive) || (filter === 'inactive' && !s.isActive) || s.religion.toLowerCase() === filter.toLowerCase();
    return isTabMatch && matchSearch && matchFilter;
  });

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Build stats from service stats
  const totalServices = services.length;
  const activeServices = services.filter(s => s.isActive).length;
  const totalBookings = serviceStats.reduce((sum, s) => sum + (s.totalBookings || 0), 0);
  const totalRevenue = serviceStats.reduce((sum, s) => sum + (s.totalRevenue || 0), 0);

  const stats = [
    { label: 'Total Services', val: totalServices.toString(), sub: `${activeServices} active`, icon: '', color: '#2563eb' },
    { label: 'Active',         val: activeServices.toString(), sub: 'Currently offered', icon: '', color: '#10b981' },
    { label: 'Total Bookings', val: totalBookings.toLocaleString(), sub: 'Across all services', icon: '', color: '#8b5cf6' },
    { label: 'Revenue',        val: `₹${totalRevenue.toLocaleString()}`, sub: 'From services', icon: '', color: '#f59e0b' },
  ];

  const handleToggleActive = async (svc: ServiceData) => {
    setProcessing(true);
    try {
      await updateService(svc.id, { isActive: !svc.isActive });
      showToast(`Service ${svc.isActive ? 'deactivated' : 'activated'}`);
      await fetchData();
    } catch {
      showToast('Failed to update service', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditClick = (svc: ServiceData) => {
    setEditingService(svc);
    setEditForm({
      basePrice: svc.basePrice || 0,
      startRange: svc.startRange || 0,
      endRange: svc.endRange || 0,
      durationMin: svc.durationMin || 0,
      platformFix: svc.platformFix || 0,
      platformPercentage: svc.platformPercentage || 0,
      isActive: svc.isActive,
      isCustom: svc.isCustom || false,
      isApproved: svc.isApproved ?? true,
      iconUrl: svc.iconUrl || '',
    });
  };

  const handleReviewClick = (svc: ServiceData) => {
    setReviewingService(svc);
    setEditForm({
      basePrice: svc.basePrice || 0,
      startRange: svc.startRange || 0,
      endRange: svc.endRange || 0,
      durationMin: svc.durationMin || 30,
      platformFix: svc.platformFix || 0,
      platformPercentage: svc.platformPercentage || 0,
      isActive: true,
      isCustom: true,
      isApproved: true,
      iconUrl: svc.iconUrl || '',
    });
  };

  const handleApproveCustomService = async () => {
    if (!reviewingService) return;
    if (editForm.startRange && editForm.endRange && editForm.startRange > editForm.endRange) {
      showToast('Start range cannot be greater than end range', 'error');
      return;
    }
    setProcessing(true);
    try {
      const payload: any = {
        basePrice: Number(editForm.basePrice) || 0,
        startRange: Number(editForm.startRange) || 0,
        endRange: Number(editForm.endRange) || 0,
        durationMin: Number(editForm.durationMin) || reviewingService.durationMin || 30,
        platformFix: Number(editForm.platformFix) || 0,
        platformPercentage: Number(editForm.platformPercentage) || 0,
        isActive: true,
        isCustom: true,
        isApproved: true,
      };
      if (editForm.iconUrl && editForm.iconUrl.trim()) {
        payload.iconUrl = editForm.iconUrl.trim();
      }
      await updateService(reviewingService.id, payload);
      showToast('Custom service approved & activated');
      setReviewingService(null);
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to approve service';
      showToast(msg, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectCustomService = async () => {
    if (!reviewingService) return;
    setProcessing(true);
    try {
      await updateService(reviewingService.id, { isApproved: false, isActive: false });
      showToast('Custom service rejected');
      setReviewingService(null);
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to reject service';
      showToast(msg, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingService) return;
    if (editForm.startRange && editForm.endRange && editForm.startRange > editForm.endRange) {
      showToast('Start range cannot be greater than end range', 'error');
      return;
    }
    setProcessing(true);
    try {
      const payload: any = { ...editForm };
      if (!payload.iconUrl || !payload.iconUrl.trim()) {
        delete payload.iconUrl;
      }
      await updateService(editingService.id, payload);
      showToast('Service updated successfully');
      setEditingService(null);
      await fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update service';
      showToast(msg, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateService = async () => {
    if (!createForm.name || !createForm.religion) {
      showToast('Name and Religion are required', 'error');
      return;
    }
    if (createForm.startRange > createForm.endRange) {
      showToast('Start range cannot be greater than end range', 'error');
      return;
    }
    setProcessing(true);
    try {
      await createService(createForm);
      showToast('Service created successfully');
      setIsCreating(false);
      setCreateForm({ name: '', description: '', religion: '', basePrice: 0, startRange: 0, endRange: 0, durationMin: 60, platformFix: 0, platformPercentage: 0, isCustom: false, iconUrl: '' });
      await fetchData();
    } catch {
      showToast('Failed to create service', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const loadReligions = async () => {
    const configs = await getConfig('general');
    const relConfig = configs.find(c => c.key === 'SUPPORTED_RELIGIONS');
    if (relConfig) {
      setReligions(relConfig.value.split(',').map((s: string) => s.trim()));
    } else {
      // Default fallback
      setReligions(['ISLAM', 'HINDUISM', 'CHRISTIANITY']);
    }
  };

  const handleOpenManageReligions = async () => {
    await loadReligions();
    setIsManagingReligions(true);
  };

  const handleAddReligion = async () => {
    if (!newReligion) return;
    const updated = [...religions, newReligion.toUpperCase().trim()];
    const unique = Array.from(new Set(updated));
    setProcessing(true);
    try {
      await updateConfig([{ key: 'SUPPORTED_RELIGIONS', value: unique.join(','), category: 'general' }]);
      setReligions(unique);
      setNewReligion('');
      showToast('Religions updated');
    } catch {
      showToast('Failed to update' ,'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveReligion = async (rel: string) => {
    const updated = religions.filter(r => r !== rel);
    setProcessing(true);
    try {
      await updateConfig([{ key: 'SUPPORTED_RELIGIONS', value: updated.join(','), category: 'general' }]);
      setReligions(updated);
      showToast('Religion removed');
    } catch {
      showToast('Failed to update' ,'error');
    } finally {
      setProcessing(false);
    }
  };

  // Get stats for a service
  const getStats = (id: string) => serviceStats.find((s: any) => s.serviceId === id);

  const handleExportCSV = () => {
    const exportData = services.map(s => {
      const st = getStats(s.id);
      return {
        Name: s.name,
        Religion: s.religion,
        BasePrice: s.basePrice,
        StartRange: s.startRange,
        EndRange: s.endRange,
        Duration: s.durationMin,
        Status: s.isActive ? 'Active' : 'Inactive',
        IsCustom: s.isCustom ? 'Yes' : 'No',
        TotalBookings: st?.totalBookings || 0,
        TotalRevenue: st?.totalRevenue || 0,
      };
    });
    exportToCSV(exportData, 'Services_Report');
  };

  const tabStyle = (key: 'standard' | 'custom') => ({
    padding: '12px 20px', fontSize: 13, fontWeight: activeTab === key ? 700 : 500,
    color: activeTab === key ? '#2563eb' : '#64748b', cursor: 'pointer',
    borderBottom: activeTab === key ? '2.5px solid #2563eb' : '2.5px solid transparent',
    background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
    outline: 'none', whiteSpace: 'nowrap' as const, transition: 'all .15s',
  });

  const customServicesCount = services.filter(s => s.isCustom).length;
  const standardServicesCount = services.length - customServicesCount;

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader
        breadcrumb="Dashboard > Services"
        title="Service Management"
        subtitle="Manage religious services offered across the platform."
        onRefresh={fetchData}
        onExport={handleExportCSV}
      />

      {/* Stats Grid */}
      <div style={statsGrid}>
        {stats.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={labelStyle}>{s.label}</span>
              <span>{s.icon}</span>
            </div>
            <div style={valueStyle}>{s.val}</div>
            <div style={subTextStyle}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Religion Distribution & Top Services */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Religion Distribution</h3>
          {religionData.length > 0 ? religionData.map((r: any, i: number) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: ['#f97316', '#10b981', '#8b5cf6', '#3b82f6', '#06b6d4'][i % 5] }} />
                <span style={{ fontSize: 14, color: '#475569' }}>{r.religion}</span>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{r.percentage}%</span>
            </div>
          )) : <p style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</p>}
        </div>
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Top Services</h3>
          {serviceStats.slice(0, 5).map((s: any, i: number) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.totalBookings} bookings</div>
              </div>
              <div style={{ fontWeight: 700, color: '#10b981', fontSize: 14 }}>₹{s.totalRevenue.toLocaleString()}</div>
            </div>
          ))}
          {serviceStats.length === 0 && <p style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</p>}
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 24 }}>
        <button onClick={() => { setActiveTab('standard'); setPage(1); }} style={tabStyle('standard')}>
          Standard Services ({standardServicesCount})
        </button>
        <button onClick={() => { setActiveTab('custom'); setPage(1); }} style={tabStyle('custom')}>
          Custom Services ({customServicesCount})
        </button>
      </div>

      {/* Table */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search services…"
            style={{ ...tableSearch, flex: 1, minWidth: 220 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleOpenManageReligions} style={{ padding: '0 12px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>Manage Religions</button>
            <button onClick={() => { setIsCreating(true); loadReligions(); }} style={{ padding: '0 16px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>＋ Add Service</button>
            <select style={smallSelect} value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '1px solid #f1f5f9' }}>
              <th style={thStyle}>SERVICE</th>
              <th style={thStyle}>RELIGION</th>
              <th style={thStyle}>BASE PRICE</th>
              <th style={thStyle}>RATE RANGE</th>
              <th style={thStyle}>DURATION</th>
              <th style={thStyle}>BOOKINGS</th>
              <th style={thStyle}>REVENUE</th>
              <th style={thStyle}>STATUS</th>
              <th style={thStyle}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading services…</td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No services found.</td></tr>
            ) : paged.map((svc) => {
              const st = getStats(svc.id);
              return (
                <tr key={svc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {svc.iconUrl ? (
                        <img src={svc.iconUrl} alt={svc.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                          ✨
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{svc.name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{svc.description?.slice(0, 40) || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}><span style={badgeStyle}>{svc.religion}</span></td>
                  <td style={tdStyle}><span style={{ fontWeight: 700 }}>₹{svc.basePrice}</span></td>
                  <td style={tdStyle}>₹{svc.startRange || 0} - ₹{svc.endRange || 0}</td>
                  <td style={tdStyle}>{svc.durationMin} min</td>
                  <td style={tdStyle}>{st?.totalBookings || 0}</td>
                  <td style={tdStyle}><span style={{ fontWeight: 700, color: '#10b981' }}>₹{(st?.totalRevenue || 0).toLocaleString()}</span></td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: svc.isActive ? '#f0fdf4' : '#fef2f2',
                      color: svc.isActive ? '#15803d' : '#b91c1c',
                    }}>
                      {svc.isActive ? '● Active' : '● Inactive'}
                    </span>
                    {svc.isCustom && !svc.isApproved && (
                      <span style={{
                        marginLeft: 8, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: '#fef3c7', color: '#d97706', display: 'inline-block', marginTop: 4
                      }}>
                        Pending Approval
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {svc.isCustom && !svc.isApproved ? (
                      <button
                        onClick={() => handleReviewClick(svc)}
                        disabled={processing}
                        style={{
                          border: 'none', background: '#f59e0b', color: '#fff',
                          fontWeight: 700, fontSize: 12, padding: '6px 12px', borderRadius: 8,
                          cursor: processing ? 'not-allowed' : 'pointer',
                          opacity: processing ? 0.6 : 1
                        }}
                      >
                        Review
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleToggleActive(svc)}
                          disabled={processing}
                          style={{
                            border: 'none', background: svc.isActive ? '#fef2f2' : '#f0fdf4',
                            color: svc.isActive ? '#b91c1c' : '#15803d',
                            fontWeight: 700, fontSize: 12, padding: '6px 12px', borderRadius: 8,
                            cursor: processing ? 'not-allowed' : 'pointer',
                            opacity: processing ? 0.6 : 1
                          }}
                        >
                          {processing && svc.isActive ? '...' : (svc.isActive ? 'Deactivate' : 'Activate')}
                        </button>
                        <button
                          onClick={() => handleEditClick(svc)}
                          disabled={processing}
                          style={{
                            border: 'none', background: '#e0f2fe', color: '#0284c7',
                            fontWeight: 700, fontSize: 12, padding: '6px 12px', borderRadius: 8,
                            cursor: processing ? 'not-allowed' : 'pointer',
                            opacity: processing ? 0.6 : 1
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Paginator total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} />
      </div>

      {/* Modal */}
      {editingService && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 12, width: 400, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Edit {editingService.name}</h3>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Base Price (₹)</label>
              <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                     value={editForm.basePrice} onChange={e => setEditForm({...editForm, basePrice: Number(e.target.value)})} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Service Icon URL (Optional)</label>
              <input type="text" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                     value={editForm.iconUrl} onChange={e => setEditForm({...editForm, iconUrl: e.target.value})} placeholder="https://cdn.example.com/icons/service-icon.png" />
              {editForm.iconUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={editForm.iconUrl} alt="Icon Preview" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Duration (min)</label>
              <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                     value={editForm.durationMin} onChange={e => setEditForm({...editForm, durationMin: Number(e.target.value)})} />
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Start Range (₹)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }}
                       value={editForm.startRange} onChange={e => setEditForm({...editForm, startRange: Number(e.target.value)})} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>End Range (₹)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }}
                       value={editForm.endRange} onChange={e => setEditForm({...editForm, endRange: Number(e.target.value)})} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Platform Fix (₹)</label>
              <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                     value={editForm.platformFix} onChange={e => setEditForm({...editForm, platformFix: Number(e.target.value)})} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Platform Percentage (%)</label>
              <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                     value={editForm.platformPercentage} onChange={e => setEditForm({...editForm, platformPercentage: Number(e.target.value)})} />
            </div>

            <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Service Status</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Toggle to activate or deactivate this service</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: 48, height: 26, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: editForm.isActive ? '#10b981' : '#cbd5e1',
                    transition: '.3s', borderRadius: 34,
                  }}>
                    <span style={{
                      position: 'absolute', height: 20, width: 20,
                      left: editForm.isActive ? '24px' : '3px', bottom: 3,
                      backgroundColor: 'white', transition: '.3s', borderRadius: '50%',
                    }} />
                  </span>
                </label>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: editForm.isActive ? '#15803d' : '#b91c1c' }}>
                {editForm.isActive ? '● Active — visible to users' : '● Inactive — hidden from users'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Custom Service</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Mark this as a custom, unlisted service</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: 48, height: 26, cursor: 'pointer' }}>
                  <input type="checkbox" checked={editForm.isCustom} onChange={e => setEditForm({ ...editForm, isCustom: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: editForm.isCustom ? '#3b82f6' : '#cbd5e1', transition: '.3s', borderRadius: 34 }}>
                    <span style={{ position: 'absolute', height: 20, width: 20, left: editForm.isCustom ? '24px' : '3px', bottom: 3, backgroundColor: 'white', transition: '.3s', borderRadius: '50%' }} />
                  </span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setEditingService(null)} disabled={processing} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>Cancel</button>
              <button onClick={handleSaveEdit} disabled={processing} style={{ padding: '8px 16px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewingService && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 12, width: 400, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Approve Custom Service</h3>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Name</label>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{reviewingService.name}</div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Description</label>
              <div style={{ fontSize: 14 }}>{reviewingService.description || 'No description'}</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Scholar's Base Price & Duration</label>
              <div style={{ fontSize: 14 }}>₹{reviewingService.basePrice} for {reviewingService.durationMin} min</div>
            </div>
            
            <div style={{ borderTop: '1px solid #e2e8f0', margin: '16px 0', paddingTop: 16 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>Set Platform Rates</h4>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Base Price (₹) - Modifiable</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={editForm.basePrice} onChange={e => setEditForm({...editForm, basePrice: Number(e.target.value)})} />
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Start Range (₹)</label>
                  <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }}
                         value={editForm.startRange} onChange={e => setEditForm({...editForm, startRange: Number(e.target.value)})} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>End Range (₹)</label>
                  <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }}
                         value={editForm.endRange} onChange={e => setEditForm({...editForm, endRange: Number(e.target.value)})} />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Platform Fix (₹)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={editForm.platformFix} onChange={e => setEditForm({...editForm, platformFix: Number(e.target.value)})} />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Platform Percentage (%)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={editForm.platformPercentage} onChange={e => setEditForm({...editForm, platformPercentage: Number(e.target.value)})} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setReviewingService(null)} disabled={processing} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>Cancel</button>
              <button onClick={handleRejectCustomService} disabled={processing} style={{ padding: '8px 16px', border: 'none', background: '#ef4444', color: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>
                Reject
              </button>
              <button onClick={handleApproveCustomService} disabled={processing} style={{ padding: '8px 16px', border: 'none', background: '#10b981', color: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>
                {processing ? 'Processing...' : 'Approve & Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Modal */}
      {isCreating && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 12, width: 500, maxWidth: '95%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Add New Service</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Service Name</label>
                <input type="text" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="e.g. Wedding Puja" />
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Description</label>
                <textarea style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box', height: 60 }} 
                       value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} placeholder="Brief description..." />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Religion</label>
                <select style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                        value={createForm.religion} onChange={e => setCreateForm({...createForm, religion: e.target.value})}>
                  <option value="">Select Religion</option>
                  {religions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Service Icon URL (Optional)</label>
                <input type="text" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={createForm.iconUrl} onChange={e => setCreateForm({...createForm, iconUrl: e.target.value})} placeholder="https://.../icon.png" />
                {createForm.iconUrl && (
                  <div style={{ marginTop: 8 }}>
                    <img src={createForm.iconUrl} alt="Icon Preview" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Base Price (₹)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={createForm.basePrice} onChange={e => setCreateForm({...createForm, basePrice: Number(e.target.value)})} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Duration (min)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                       value={createForm.durationMin} onChange={e => setCreateForm({...createForm, durationMin: Number(e.target.value)})} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Start Range (₹)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }}
                       value={createForm.startRange} onChange={e => setCreateForm({...createForm, startRange: Number(e.target.value)})} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>End Range (₹)</label>
                <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }}
                       value={createForm.endRange} onChange={e => setCreateForm({...createForm, endRange: Number(e.target.value)})} />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>PF Fix (₹)</label>
                  <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                         value={createForm.platformFix} onChange={e => setCreateForm({...createForm, platformFix: Number(e.target.value)})} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>PF %</label>
                  <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, boxSizing: 'border-box' }} 
                         value={createForm.platformPercentage} onChange={e => setCreateForm({...createForm, platformPercentage: Number(e.target.value)})} />
                </div>
              </div>
            </div>

            {/* Custom service boundary */}
            <div style={{ marginTop: 20, padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Custom Service</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Mark if this is requested uniquely by a scholar</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: 48, height: 26, cursor: 'pointer' }}>
                  <input type="checkbox" checked={createForm.isCustom} onChange={e => setCreateForm({ ...createForm, isCustom: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: createForm.isCustom ? '#3b82f6' : '#cbd5e1', transition: '.3s', borderRadius: 34 }}>
                    <span style={{ position: 'absolute', height: 20, width: 20, left: createForm.isCustom ? '24px' : '3px', bottom: 3, backgroundColor: 'white', transition: '.3s', borderRadius: '50%' }} />
                  </span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <button onClick={() => setIsCreating(false)} disabled={processing} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>Cancel</button>
              <button onClick={handleCreateService} disabled={processing} style={{ padding: '8px 16px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 6, cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: processing ? 0.6 : 1 }}>
                {processing ? 'Creating...' : 'Create Service'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Religions Modal */}
      {isManagingReligions && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 12, width: 400, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Manage Religions</h3>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>Added religions will appear in dropdowns across the platform.</p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input 
                type="text" placeholder="NEW RELIGION..." 
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, textTransform: 'uppercase' }}
                value={newReligion} onChange={e => setNewReligion(e.target.value)}
              />
              <button onClick={handleAddReligion} style={{ padding: '0 16px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Add</button>
            </div>

            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 20, border: '1px solid #f1f5f9', borderRadius: 8 }}>
              {religions.map(r => (
                <div key={r} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{r}</span>
                  <button onClick={() => handleRemoveReligion(r)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18 }}>&times;</button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button onClick={() => setIsManagingReligions(false)} style={{ padding: '8px 24px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600, width: '100%' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;
